/* Laying out the caption band's text.
 *
 * `fillText` neither wraps nor clips: a line wider than the image is painted past its edge and
 * lost, with nothing to show it was cut. The exclusion notice is the line at risk - "9 of 12
 * components plotted. The rest have no value for one of the chosen fields." is around 390 CSS
 * pixels, and longer in German - and it is also the line whose absence lets the file claim to plot
 * everything. So the band is measured against the image it sits on and broken to fit.
 */

/** What a line of text will measure once painted, in the units `maxWidth` is given in. */
export type MeasureText = (text: string) => number;

const HORIZONTAL_ELLIPSIS = '…';

/**
 * A word cut to fit, marked as cut.
 *
 * Only reachable for a token with no space to break at that is wider than the image on its own - a
 * long field name in the color key, say. Wrapping cannot help it and an ellipsis at least says the
 * name is longer than what is shown.
 */
const ellipsize = (
  word: string,
  maxWidth: number,
  measure: MeasureText
): string => {
  if (measure(word) <= maxWidth) return word;

  const characters = [...word];

  /* The longest *prefix* that fits, rather than every character that happens to fit. Taking them
     one at a time is wrong for the only font this will ever run in: character widths are not
     monotone, so a rejected `m` followed by an accepted `i` deletes the `m` from the middle of the
     word - and a name shown misspelled is the thing the ellipsis is here to avoid. */
  const fitted = characters
    .map((_, index) => characters.slice(0, index + 1).join(''))
    .filter(prefix => measure(`${prefix}${HORIZONTAL_ELLIPSIS}`) <= maxWidth)
    .at(-1);

  return `${fitted ?? ''}${HORIZONTAL_ELLIPSIS}`;
};

const withWord = (
  lines: readonly string[],
  word: string,
  maxWidth: number,
  measure: MeasureText
): string[] => {
  const line = lines.at(-1);
  if (line === undefined) return [ellipsize(word, maxWidth, measure)];

  const extended = `${line} ${word}`;
  if (measure(extended) <= maxWidth) {
    return [...lines.slice(0, -1), extended];
  }

  return [...lines, ellipsize(word, maxWidth, measure)];
};

/** One caption as the lines it needs, greedily: as many words per line as fit. */
export const wrapCaption = (
  caption: string,
  maxWidth: number,
  measure: MeasureText
): string[] =>
  caption
    .split(/\s+/)
    .filter(word => word.length > 0)
    .reduce<string[]>(
      (lines, word) => withWord(lines, word, maxWidth, measure),
      []
    );

/**
 * Every caption as the lines the band has to hold, in order.
 *
 * The band's height comes from the length of this, not from the number of captions: a notice that
 * takes three lines needs three lines of room, or the copy of the chart below it is drawn over the
 * bottom of the text.
 */
export const wrapCaptions = (
  captions: readonly string[],
  maxWidth: number,
  measure: MeasureText
): string[] =>
  captions.flatMap(caption => wrapCaption(caption, maxWidth, measure));
